import { createAsyncThunk } from '@reduxjs/toolkit'
import { selectAccessToken } from 'redux/authToken/authTokenSlice'
import { updateDemographicInfoState } from 'redux/demographic/demographicSlice'
import { selectAccountId } from 'redux/loginInfo/loginInfoSelectors'
import type { OriginalRootState } from 'redux/rootReducer'
import onlineUserAPI from 'services/onlineUser'
import type {
  OnlineUserDetailResponse,
  OnlineUserListRequest
} from 'typings/onlineUser'
import type { ServerError } from 'typings/serverError'
// eslint-disable-next-line import/no-cycle
import {
  selectDetailsExpandedUserId,
  updateOnlineUserState
} from './onlineUserSlice'
import type {
  AddOnlineUserRequest,
  ChildAccountPrivilegeRequest,
  GetUserAvailabilityPayload,
  ResetOnlineUserPasswordRequest,
  UpdateUserContact,
  UpdateUserIdRequest,
  UpdateUserPriviliges
} from './typings'

export const getOnlineUserList = createAsyncThunk(
  'onlineUserSlice/onlineUserSlice',
  async (payload: OnlineUserListRequest, { dispatch }) => {
    try {
      const response = await onlineUserAPI.getOnlineUserList(payload)
      const { onlineUsers } = response
      const dataList = onlineUsers.map(item => {
        const { firstName, lastName, userStatus } = item
        return {
          ...item,
          fullName: `${firstName} ${lastName}`,
          userStatus: userStatus || 'pending'
        }
      })

      dispatch(
        updateOnlineUserState({
          dataList,
          isDataLoading: false,
          error: false,
          errorMessage: ''
        })
      )
    } catch (error) {
      const { response } = error as ServerError
      const { message } = response || {}

      dispatch(
        updateOnlineUserState({
          dataList: [],
          isDataLoading: false,
          error: true,
          errorMessage: message
        })
      )
    }
  }
)

export const getOnlineUserDetail = createAsyncThunk(
  'onlineUserSlice/getOnlineUserDetail',
  async (payload: OnlineUserListRequest, { dispatch }) => {
    try {
      const response = await onlineUserAPI.getOnlineUserDetail(payload)
      dispatch(
        updateOnlineUserState({
          error: false,
          errorMessage: '',
          onlineUserDetail: response,
          errorCode: 'USER_EXISTS',
          isDetailDataLoading: false
        })
      )
    } catch (error) {
      const { response } = error as ServerError
      const { message, businessErrors = [] } = response || {}
      const { code = '' } = businessErrors[0]

      dispatch(
        updateOnlineUserState({
          isDetailDataLoading: false,
          error: !code,
          errorMessage: message,
          onlineUserDetail: {} as OnlineUserDetailResponse,
          errorCode: code
        })
      )
    }
  }
)

interface ToggleUserMfaPayload {
  newIsMfaEnabledState: boolean
}

/*
 * Since there is no loader to let the user know that the update is pending,
 * we are changing the toggle switch state as soon as the user clicks on it instead of waiting for the 204 response.
 * This way the user will see that something happened and will not try to toggle the switch multiple times.
 *
 * Due to the app changing the MFA state when the action is pending, we need to send the new state to the thunk.
 * If we were to get the state directly in here it could be outdated since its being changed in the action pending listener.
 */
export const toggleUserMfa = createAsyncThunk(
  'onlineUserSlice/toggleUserMfa',
  async (payload: ToggleUserMfaPayload, { getState, rejectWithValue }) => {
    const state = getState() as OriginalRootState

    const { newIsMfaEnabledState } = payload
    const accessToken = selectAccessToken(state)
    const userId = selectDetailsExpandedUserId(state)
    const accountId = selectAccountId(state)

    try {
      return await onlineUserAPI.toggleMfa({
        accessToken,
        userId,
        accountId,
        newIsMfaEnabledState
      })
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deactivateOnlineUser = createAsyncThunk(
  'onlineUserSlice/deactivateOnlineUser',
  async (payload: OnlineUserListRequest, { dispatch }) => {
    try {
      await onlineUserAPI.deactivateOnlineUser(payload)

      dispatch(
        updateOnlineUserState({
          isDetailDataLoading: false,
          error: false,
          errorMessage: '',
          isDeactivateSuccess: true
        })
      )
    } catch (error) {
      const { response } = error as ServerError
      const { message } = response || {}

      dispatch(
        updateOnlineUserState({
          isDetailDataLoading: false,
          isDeactivateSuccess: false,
          error: true,
          errorMessage: message,
          onlineUserDetail: {} as OnlineUserDetailResponse
        })
      )
    }
  }
)

/*
 * PENDING API ENDPOINT
 * simulateApiDelay is just a temporary stub to give a real world like experience -
 * so that QA can test the intended behavior. We will remove this once we get -
 * an endpoint back from API Team.
 */

export const simulateApiDelay = () => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve('Delayed dispatch is complete')
    }, 1000)
  })
}

export const updateOnlineUserInformation = createAsyncThunk(
  'authenticationSlice/updateMfaContactInfo',
  async (payload: UpdateUserContact, { dispatch }) => {
    try {
      /*
       * PENDING API ENDPOINT
       * Replace await simulateApiDelay with onlineUserAPI.updateOnlineUserInformation(payload)
       * Payload will hold accessToken and userId if required
       */
      await simulateApiDelay()

      dispatch(
        updateOnlineUserState({
          error: false,
          errorMessage: '',
          isSuccess: true
        })
      )
    } catch (error) {
      /*
       * PENDING API ENDPOINT
       * Will need to replace with API error
       * const { response } = error as ServerError
       * const { message } = response || {}
       */
      dispatch(
        updateOnlineUserState({
          error: false,
          errorMessage: 'Something wrong happened',
          isSuccess: false
        })
      )
    }
  }
)

export const updateOnlineUserPrivilege = createAsyncThunk(
  'onlineUserSlice/updateOnlineUserPrivilege',
  async (payload: UpdateUserPriviliges, { dispatch }) => {
    try {
      await onlineUserAPI.updateOnlineUserPrivilege(payload)

      dispatch(
        updateOnlineUserState({
          error: false,
          errorMessage: '',
          isSuccess: true
        })
      )
    } catch (error) {
      const { response } = error as ServerError
      const { message } = response || {}

      dispatch(
        updateOnlineUserState({
          error: true,
          errorMessage: message,
          isSuccess: false
        })
      )
    }
  }
)

export const addOnlineUser = createAsyncThunk(
  'onlineUserSlice/addOnlineUser',
  async (payload: AddOnlineUserRequest, { rejectWithValue }) => {
    try {
      return await onlineUserAPI.addOnlineUser(payload)
    } catch (errorResponse) {
      return rejectWithValue(errorResponse)
    }
  }
)

export const resetOnlineUserPassword = createAsyncThunk(
  'onlineUserSlice/resetOnlineUserPassword',
  async (
    payload: Omit<ResetOnlineUserPasswordRequest, 'accessToken'>,
    { getState, rejectWithValue }
  ) => {
    const state = getState() as OriginalRootState
    const accessToken = state.authTokenInfo?.tokenInfo.access_token

    try {
      await onlineUserAPI.resetOnlineUserPassword({ ...payload, accessToken })
      return {}
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const changeUserId = createAsyncThunk(
  'onlineUserSlice/changeUserId',
  async (payload: UpdateUserIdRequest, { dispatch }) => {
    try {
      await onlineUserAPI.changeUserId(payload)

      dispatch(
        updateOnlineUserState({
          error: false,
          errorMessage: '',
          errorCode: '',
          isUpdateSuccess: true
        })
      )
    } catch (error) {
      const {
        response: { message },
        statusCode
      } = error as ServerError

      dispatch(
        updateOnlineUserState({
          error: true,
          errorMessage: statusCode || message,
          isUpdateSuccess: false,
          errorCode: statusCode
        })
      )
    }
  }
)

export const updateChildAccountPrivilege = createAsyncThunk(
  'onlineUserSlice/updateAccountPrivilege',
  async (payload: ChildAccountPrivilegeRequest, { dispatch }) => {
    try {
      await onlineUserAPI.updateAccountPrivilege(payload)

      dispatch(
        updateDemographicInfoState({
          privilegeUpdateStatus: 'success',
          errorMessage: ''
        })
      )
    } catch (error) {
      const {
        response: { message },
        statusCode
      } = error as ServerError

      dispatch(
        updateDemographicInfoState({
          errorMessage: `${statusCode}` || message,
          privilegeUpdateStatus: 'failure'
        })
      )
    }
  }
)

export const getUserAvailability = createAsyncThunk(
  'onlineUserSlice/getUserAvailability',
  async (payload: GetUserAvailabilityPayload, { dispatch }) => {
    try {
      await onlineUserAPI.checkUserAvailability(payload)

      dispatch(
        updateOnlineUserState({
          error: false,
          errorCode: 'USER_EXISTS'
        })
      )
    } catch (error) {
      const { response } = error as ServerError
      const { businessErrors = [] } = response || {}
      const { code = '' } = businessErrors[0] || {}

      dispatch(
        updateOnlineUserState({
          error: false,
          errorCode: code
        })
      )
    }
  }
)
